import {useVirtualizer} from '@tanstack/react-virtual';
import {useRef, useState, type ReactNode} from 'react';
import {tr, trx} from '../i18n/idioma';
import {CajaAjustable} from './caja-ajustable';
import {moverColumna, type ColumnaDeTabla, type DefinicionDeColumnas} from './columnas';
import {NotaDeAyuda} from './nota-de-ayuda';

type Props<T, O extends string> = {
  filas: T[];
  definicion: DefinicionDeColumnas<T, O>;
  columnasVisibles: string[];
  claveDeFila: (fila: T) => string | number;
  orden: O;
  direccion: 'asc' | 'desc';
  onOrdenar: (orden: O) => void;
  onReordenar: (ids: string[]) => void;
  cargando?: boolean;
  mensajeVacio: string;
  // Dibujo especial de una celda (enlaces, chips). Si devuelve undefined se usa el texto.
  celda?: (columna: ColumnaDeTabla<T, O>, fila: T) => ReactNode | undefined;
  altoMaximo?: string;
  // Casillas de seleccion al inicio de cada fila (y una en el encabezado para toda la pagina).
  seleccion?: {
    seleccionada: (fila: T) => boolean;
    onAlternar: (filas: T[], seleccionar: boolean) => void;
  };
  // Numero de linea (#) al inicio de cada fila, contando desde aqui: con
  // paginacion es lo que va antes de la pagina ((pagina - 1) x por pagina).
  numeracionDesde?: number;
  // Color de fondo propio de una fila (por ejemplo, amarillo para revisar).
  claseDeFila?: (fila: T) => string | undefined;
};

const ALTO_DE_FILA = 34;
const ANCHO_DE_SELECCION = 36;
const ANCHO_DE_NUMERO = 52;
const FILAS_POR_SALTO = 10;
// Un clic en estos elementos hace lo suyo (enlace, edicion, casilla), no selecciona la fila.
const INTERACTIVOS = 'a, button, input, select, textarea, label, [contenteditable="true"]';

// Tabla de consulta: las filas visibles se dibujan segun se desplaza la
// pantalla (virtualizada), asi una pagina de 500 filas no pesa. Los
// encabezados se pueden arrastrar para cambiar el orden de las columnas.
export function Tabla<T, O extends string>({
  filas,
  definicion,
  columnasVisibles,
  claveDeFila,
  orden,
  direccion,
  onOrdenar,
  onReordenar,
  cargando = false,
  mensajeVacio,
  celda,
  altoMaximo = 'calc(100vh - 23rem)',
  seleccion,
  numeracionDesde,
  claseDeFila,
}: Props<T, O>) {
  const numerada = numeracionDesde !== undefined;
  const contenedor = useRef<HTMLDivElement>(null);
  const tabla = useRef<HTMLTableElement>(null);
  const [arrastrando, setArrastrando] = useState<string | null>(null);
  const [destino, setDestino] = useState<string | null>(null);
  const columnas = columnasVisibles
    .map(id => definicion.columnas.find(c => c.id === id))
    .filter((c): c is ColumnaDeTabla<T, O> => c !== undefined);
  const virtual = useVirtualizer({
    count: filas.length,
    getScrollElement: () => contenedor.current,
    estimateSize: () => ALTO_DE_FILA,
    overscan: 12,
  });
  const elementos = virtual.getVirtualItems();
  const espacioArriba = elementos.length ? elementos[0]!.start : 0;
  const espacioAbajo = elementos.length ? virtual.getTotalSize() - elementos[elementos.length - 1]!.end : 0;
  const anchoTotal = columnas.reduce(
    (a, c) => a + (c.ancho ?? 120),
    (seleccion ? ANCHO_DE_SELECCION : 0) + (numerada ? ANCHO_DE_NUMERO : 0),
  );
  const totalDeColumnas = columnas.length + (seleccion ? 1 : 0) + (numerada ? 1 : 0);
  // Al moverse a la derecha quedan fijas la seleccion, el numero de linea y la
  // columna llave (Exportacion, BB #) si va de primera (Daniel, 9/10/2026).
  const izquierdaDelNumero = seleccion ? ANCHO_DE_SELECCION : 0;
  const izquierdaDeLaLlave = izquierdaDelNumero + (numerada ? ANCHO_DE_NUMERO : 0);
  const llaveFija = definicion.fija !== undefined && columnas[0]?.id === definicion.fija;
  const fija = (izquierda: number, encabezado: boolean, ultima = false) =>
    ({
      position: 'sticky',
      left: izquierda,
      zIndex: encabezado ? 20 : 5,
      ...(ultima ? {boxShadow: '1px 0 0 var(--color-linea)'} : {}),
    }) as const;
  // Ancho exacto para las columnas fijas de seleccion y numero: si crecen, la
  // siguiente columna fija queda corrida y se ve lo que pasa por debajo.
  const anchoExacto = (ancho: number) => ({
    width: ancho,
    minWidth: ancho,
    maxWidth: ancho,
    boxSizing: 'border-box' as const,
  });
  const ultimaFija = llaveFija ? 'llave' : numerada ? 'numero' : 'seleccion';
  // Las celdas fijas necesitan fondo propio para tapar lo que pasa por debajo.
  const fondoFijo = (marcada: boolean) => (marcada ? 'bg-verde-claro' : 'bg-white group-hover:bg-verde-claro');
  const todasSeleccionadas = seleccion !== undefined && filas.length > 0 && filas.every(seleccion.seleccionada);

  // Seleccion como en Excel (Daniel, 8/10/2026). La fila activa es la del
  // cursor; el ancla es donde empieza un rango con Shift. Clic o flecha:
  // solo esa fila. Ctrl: agrega o quita una. Shift: el rango desde el ancla.
  // Los rangos y "solo esta" se aplican sobre la pagina que se ve.
  const [activa, setActiva] = useState<number | null>(null);
  const [ancla, setAncla] = useState<number | null>(null);
  const filaActiva = activa !== null && activa < filas.length ? activa : null;
  const anclaValida = ancla !== null && ancla < filas.length ? ancla : null;

  const elegirRango = (desde: number, hasta: number, sumar: boolean) => {
    if (!seleccion) return;
    const rango = filas.slice(Math.min(desde, hasta), Math.max(desde, hasta) + 1);
    if (!sumar) {
      const quitar = filas.filter(f => !rango.includes(f) && seleccion.seleccionada(f));
      if (quitar.length) seleccion.onAlternar(quitar, false);
    }
    const poner = rango.filter(f => !seleccion.seleccionada(f));
    if (poner.length) seleccion.onAlternar(poner, true);
  };

  const irA = (indice: number, modo: 'solo' | 'rango' | 'sumarRango') => {
    if (!seleccion || !filas.length) return;
    const destinoFila = Math.max(0, Math.min(filas.length - 1, indice));
    setActiva(destinoFila);
    virtual.scrollToIndex(destinoFila, {align: 'auto'});
    if (modo === 'solo') {
      setAncla(destinoFila);
      elegirRango(destinoFila, destinoFila, false);
      return;
    }
    const inicio = anclaValida ?? filaActiva ?? destinoFila;
    if (anclaValida === null) setAncla(inicio);
    elegirRango(inicio, destinoFila, modo === 'sumarRango');
  };

  const alternarUna = (indice: number) => {
    if (!seleccion) return;
    const fila = filas[indice]!;
    setActiva(indice);
    setAncla(indice);
    seleccion.onAlternar([fila], !seleccion.seleccionada(fila));
  };

  const clicEnFila = (evento: React.MouseEvent, indice: number) => {
    if (!seleccion || (evento.target as HTMLElement).closest(INTERACTIVOS)) return;
    const control = evento.ctrlKey || evento.metaKey;
    if (evento.shiftKey) irA(indice, control ? 'sumarRango' : 'rango');
    else if (control) alternarUna(indice);
    else irA(indice, 'solo');
  };

  const teclaEnTabla = (evento: React.KeyboardEvent) => {
    if (!seleccion || !filas.length) return;
    const objetivo = evento.target as HTMLElement;
    if (objetivo.closest('input, select, textarea, [contenteditable="true"]')) return;
    const control = evento.ctrlKey || evento.metaKey;
    const actual = filaActiva ?? -1;
    let destinoFila: number;
    switch (evento.key) {
      case 'ArrowDown':
        destinoFila = control ? filas.length - 1 : actual + 1;
        break;
      case 'ArrowUp':
        destinoFila = control ? 0 : Math.max(actual - 1, 0);
        break;
      case 'PageDown':
        destinoFila = actual + FILAS_POR_SALTO;
        break;
      case 'PageUp':
        destinoFila = Math.max(actual - FILAS_POR_SALTO, 0);
        break;
      case 'Home':
        destinoFila = 0;
        break;
      case 'End':
        destinoFila = filas.length - 1;
        break;
      case ' ':
        if (objetivo.closest('a, button')) return;
        evento.preventDefault();
        alternarUna(Math.max(actual, 0));
        return;
      case 'a':
      case 'A':
        if (!control) return;
        evento.preventDefault();
        seleccion.onAlternar(
          filas.filter(f => !seleccion.seleccionada(f)),
          true,
        );
        return;
      case 'Escape': {
        const marcadas = filas.filter(seleccion.seleccionada);
        if (!marcadas.length) return;
        evento.preventDefault();
        seleccion.onAlternar(marcadas, false);
        return;
      }
      default:
        return;
    }
    evento.preventDefault();
    // Con Shift se extiende el rango desde el ancla; sin Shift queda solo esa
    // fila. Ctrl + flecha salta a la primera o la ultima, como en Excel.
    irA(destinoFila, evento.shiftKey ? 'rango' : 'solo');
  };

  const soltar = (sobre: string) => {
    if (arrastrando && arrastrando !== sobre) onReordenar(moverColumna(columnasVisibles, arrastrando, sobre));
    setArrastrando(null);
    setDestino(null);
  };

  return (
    <CajaAjustable
      ref={contenedor}
      altoMaximo={altoMaximo}
      className={`relative min-h-[10rem] overflow-auto rounded-lg border border-linea bg-white ${cargando ? 'opacity-60' : ''}`}
    >
      <table
        ref={tabla}
        className="border-separate border-spacing-0 text-sm focus:outline-none"
        style={{minWidth: `${anchoTotal}px`, width: '100%'}}
        tabIndex={seleccion ? 0 : undefined}
        onKeyDown={seleccion ? teclaEnTabla : undefined}
        aria-label={
          seleccion
            ? tr(
                'Tabla: clic, Ctrl + clic y Shift + clic, o flechas con Shift, para seleccionar filas',
                'Table: click, Ctrl + click and Shift + click, or arrows with Shift, to select rows',
              )
            : undefined
        }
      >
        <thead className="sticky top-0 z-10 bg-papel">
          <tr>
            {seleccion && (
              <th
                scope="col"
                style={{...anchoExacto(ANCHO_DE_SELECCION), ...fija(0, true, ultimaFija === 'seleccion')}}
                className="border-b border-linea bg-papel px-2 py-2 text-center"
              >
                <input
                  type="checkbox"
                  checked={todasSeleccionadas}
                  onChange={e => seleccion.onAlternar(filas, e.target.checked)}
                  aria-label={tr('Seleccionar todas las filas de esta página', 'Select all rows on this page')}
                  title={tr('Seleccionar todas las filas de esta página', 'Select all rows on this page')}
                />
              </th>
            )}
            {numerada && (
              <th
                scope="col"
                style={{...anchoExacto(ANCHO_DE_NUMERO), ...fija(izquierdaDelNumero, true, ultimaFija === 'numero')}}
                className="border-b border-linea bg-papel px-2 py-2 text-right text-xs font-semibold text-apagado"
                title={tr('Número de línea', 'Line number')}
              >
                #
              </th>
            )}
            {columnas.map(columna => {
              const ordenable = columna.orden !== undefined;
              const activa = columna.orden === orden;
              const esDestino = destino === columna.id && arrastrando !== columna.id;
              return (
                <th
                  key={columna.id}
                  scope="col"
                  style={{
                    width: columna.ancho,
                    ...(llaveFija && columna.id === definicion.fija
                      ? {...fija(izquierdaDeLaLlave, true, true), background: 'var(--color-papel)'}
                      : {}),
                  }}
                  draggable
                  onDragStart={e => {
                    setArrastrando(columna.id);
                    e.dataTransfer.effectAllowed = 'move';
                    e.dataTransfer.setData('text/plain', columna.id);
                  }}
                  onDragOver={e => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    if (destino !== columna.id) setDestino(columna.id);
                  }}
                  onDragLeave={() => setDestino(actual => (actual === columna.id ? null : actual))}
                  onDrop={e => {
                    e.preventDefault();
                    soltar(columna.id);
                  }}
                  onDragEnd={() => {
                    setArrastrando(null);
                    setDestino(null);
                  }}
                  title={tr('Arrastra para cambiar el orden de las columnas', 'Drag to change the column order')}
                  className={`cursor-grab border-b border-linea px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap text-apagado select-none ${
                    columna.numerica ? 'text-right' : ''
                  } ${esDestino ? 'border-l-2 border-l-verde bg-verde-claro' : ''} ${arrastrando === columna.id ? 'opacity-40' : ''}`}
                >
                  {ordenable ? (
                    <button
                      type="button"
                      onClick={() => onOrdenar(columna.orden!)}
                      className={`inline-flex items-center gap-1 hover:text-tinta ${activa ? 'text-verde-oscuro' : ''}`}
                      title={tr(`Ordenar por ${trx(columna.titulo)}`, `Sort by ${trx(columna.titulo)}`)}
                    >
                      {trx(columna.titulo)}
                      <span aria-hidden className="text-[10px]">
                        {activa ? (direccion === 'asc' ? '▲' : '▼') : '↕'}
                      </span>
                    </button>
                  ) : (
                    trx(columna.titulo)
                  )}
                  {columna.ayuda && <NotaDeAyuda titulo={columna.titulo} texto={columna.ayuda} />}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {espacioArriba > 0 && (
            <tr aria-hidden>
              <td style={{height: espacioArriba}} colSpan={totalDeColumnas} />
            </tr>
          )}
          {elementos.map(elemento => {
            const fila = filas[elemento.index]!;
            const marcada = seleccion?.seleccionada(fila) ?? false;
            const esActiva = seleccion !== undefined && filaActiva === elemento.index;
            return (
              <tr
                key={claveDeFila(fila)}
                className={`group ${marcada ? 'bg-verde-claro/60' : `hover:bg-verde-claro/40 ${claseDeFila?.(fila) ?? ''}`} ${
                  esActiva ? 'outline-2 -outline-offset-2 outline-verde' : ''
                } ${seleccion ? 'cursor-default' : ''}`}
                style={{height: ALTO_DE_FILA}}
                aria-selected={seleccion ? marcada : undefined}
                data-activa={esActiva || undefined}
                onMouseDown={
                  seleccion
                    ? e => {
                        // Shift + clic no debe marcar texto; el foco va a la tabla para seguir con el teclado.
                        if ((e.target as HTMLElement).closest(INTERACTIVOS)) return;
                        if (e.shiftKey) e.preventDefault();
                        tabla.current?.focus({preventScroll: true});
                      }
                    : undefined
                }
                onClick={seleccion ? e => clicEnFila(e, elemento.index) : undefined}
              >
                {seleccion && (
                  <td
                    className={`border-b border-linea/70 px-2 py-1.5 text-center ${fondoFijo(marcada)}`}
                    style={{...anchoExacto(ANCHO_DE_SELECCION), ...fija(0, false, ultimaFija === 'seleccion')}}
                  >
                    <input
                      type="checkbox"
                      checked={marcada}
                      onClick={e => {
                        // Shift + clic en la casilla: marca el rango desde la ultima tocada.
                        if (!e.shiftKey || anclaValida === null) return;
                        e.preventDefault();
                        irA(elemento.index, 'sumarRango');
                      }}
                      onChange={e => {
                        setActiva(elemento.index);
                        setAncla(elemento.index);
                        seleccion.onAlternar([fila], e.target.checked);
                      }}
                      aria-label={tr('Seleccionar esta fila', 'Select this row')}
                    />
                  </td>
                )}
                {numerada && (
                  <td
                    className={`border-b border-linea/70 px-2 py-1.5 text-right text-xs tabular-nums text-apagado ${fondoFijo(marcada)}`}
                    style={{
                      ...anchoExacto(ANCHO_DE_NUMERO),
                      ...fija(izquierdaDelNumero, false, ultimaFija === 'numero'),
                    }}
                    data-linea={numeracionDesde + elemento.index + 1}
                  >
                    {numeracionDesde + elemento.index + 1}
                  </td>
                )}
                {columnas.map(columna => (
                  <td
                    key={columna.id}
                    className={`border-b border-linea/70 px-3 py-1.5 whitespace-nowrap ${columna.numerica ? 'text-right tabular-nums' : ''} ${
                      llaveFija && columna.id === definicion.fija ? fondoFijo(marcada) : ''
                    }`}
                    style={
                      llaveFija && columna.id === definicion.fija ? fija(izquierdaDeLaLlave, false, true) : undefined
                    }
                  >
                    {celda?.(columna, fila) ?? columna.valor(fila)}
                  </td>
                ))}
              </tr>
            );
          })}
          {espacioAbajo > 0 && (
            <tr aria-hidden>
              <td style={{height: espacioAbajo}} colSpan={totalDeColumnas} />
            </tr>
          )}
          {!filas.length && !cargando && (
            <tr>
              <td colSpan={totalDeColumnas} className="px-3 py-10 text-center text-apagado">
                {mensajeVacio}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </CajaAjustable>
  );
}
